import { toRaw } from 'vue'
import { storeToRefs } from 'pinia'
import {
  Money,
  type DMAcceptorStartBlocker,
  type DMAcceptorStartBlockersResponse,
  type DMAcceptorStatus,
  type DMAcceptorType
} from '@nsftx/systems-sdk'
import { logService } from '@nsftx/seven-client-retail-utils'
import deviceManagementService from './deviceManagementService'
import { hasBlockersFromActedList } from './acceptorStartBlockers'
import DMMoneyError from '#src/classes/DMMoneyError'
import { useDeviceManagementAcceptorsStore } from '#src/stores/deviceManagementAcceptorsStore'
import errorParser from '#src/common/error-handling/errorParser'

let money: Money
let isMoneyModuleReady = false
let areBlockerEventActionsEnabled = false
const LOG_PREFIX = '[deviceManagementMoneyService]'
const MONEY_MODULE_READY_TIMEOUT = 60_000

const getMoneyInstance = () => {
  if (isMoneyModuleReady) return money

  throw new DMMoneyError('Money module is not yet initialized', 'T_DM_MONEY_NOT_INITIALIZED')
}

const initMoneyModule = () => {
  const core = deviceManagementService.getCore()

  return new Promise<void>((resolve, reject) => {
    const rejectTimeout = window.setTimeout(() => {
      reject(
        new DMMoneyError(
          `${LOG_PREFIX} Money instance did not receive ready event on init.`,
          'T_DM_MONEY_INIT_ERROR'
        )
      )
    }, MONEY_MODULE_READY_TIMEOUT)

    money = new Money(core, false)
    money.on('ready', () => {
      logService.info(`${LOG_PREFIX} Money module successfully initialized`, {
        code: 'T_DM_MONEY_INIT_SUCCESS'
      })
      window.clearTimeout(rejectTimeout)
      isMoneyModuleReady = true
      resolve()
    })
  })
}

const changeAcceptorStatus = (acceptor: DMAcceptorType, status: DMAcceptorStatus) => {
  if (!isMoneyModuleReady) {
    throw new DMMoneyError('Money module is not yet initialized', 'T_DM_MONEY_NOT_INITIALIZED')
  }

  return money.changeAcceptorStatus(acceptor, status)
}

const changeAcceptorStatusAll = (doStart: boolean) => {
  if (!isMoneyModuleReady) {
    throw new DMMoneyError('Money module is not yet initialized', 'T_DM_MONEY_NOT_INITIALIZED')
  }

  return money.changeAcceptorStatusAll(doStart)
}

const getAcceptorStatus = (acceptor: DMAcceptorType) => {
  if (!isMoneyModuleReady) {
    throw new DMMoneyError('Money module is not yet initialized', 'T_DM_MONEY_NOT_INITIALIZED')
  }

  return money.getAcceptorStatus(acceptor)
}

const getBlockers = () => {
  if (!isMoneyModuleReady) {
    throw new DMMoneyError('Money module is not yet initialized', 'T_DM_MONEY_NOT_INITIALIZED')
  }

  return money.getBlockers()
}

const enableBlockerEventActions = (): void => {
  areBlockerEventActionsEnabled = true
}

const handleAcceptorStartBlockersEvents = async (
  acceptor: DMAcceptorType,
  { blockers }: DMAcceptorStartBlockersResponse
) => {
  const acceptorsStore = useDeviceManagementAcceptorsStore()
  const { isAcceptorsStartBlocked, acceptorStartBlockers } = storeToRefs(acceptorsStore)
  const nextBlockers: DMAcceptorStartBlocker[] = Array.isArray(blockers) ? blockers : []

  acceptorsStore.setAcceptorStartBlockers(nextBlockers, acceptor)

  if (!areBlockerEventActionsEnabled) {
    logService.debug(
      `${LOG_PREFIX} Ignoring acceptor start/stop on ${acceptor} event until getBlockers command path has run.`,
      {
        code: 'DM_ACCEPTORS_EVENT_ACTIONS_DEFERRED',
        details: {
          blockers: toRaw(acceptorStartBlockers.value)
        }
      }
    )
    return
  }

  if (!isAcceptorsStartBlocked.value) {
    try {
      await changeAcceptorStatusAll(true)
      logService.info(
        `${LOG_PREFIX} Message to START acceptors on ${acceptor} event was successful.`,
        {
          code: 'DM_ACCEPTORS_START_EVENT_SUCCESS'
        }
      )
    } catch (error) {
      logService.error(`${LOG_PREFIX} Message to START acceptors on ${acceptor} event failed.`, {
        code: 'DM_ACCEPTORS_START_EVENT_ERROR',
        ...errorParser.parseUpstream(error)
      })
    }
    return
  }

  if (hasBlockersFromActedList(nextBlockers)) {
    try {
      await changeAcceptorStatus(acceptor, 'stop')
      logService.info(
        `${LOG_PREFIX} Message to STOP acceptors on ${acceptor} event was successful.`,
        {
          code: 'DM_ACCEPTORS_STOP_EVENT_SUCCESS'
        }
      )
    } catch (error) {
      logService.error(`${LOG_PREFIX} Message to STOP acceptors on ${acceptor} event failed.`, {
        code: 'DM_ACCEPTORS_STOP_EVENT_ERROR',
        ...errorParser.parseUpstream(error)
      })
    }
  }

  logService.debug(
    `${LOG_PREFIX} Updated list of acceptor start blockers received on ${acceptor} event.`,
    {
      code: `DM_ACCEPTORS_${acceptor.toUpperCase()}_START_BLOCKERS_CHANGE_EVENT`,
      details: {
        blockers: toRaw(acceptorStartBlockers.value)
      }
    }
  )
}

const registerMoneyEvents = () => {
  money.on('coinStartBlockers', (response) => handleAcceptorStartBlockersEvents('coin', response))
  money.on('billStartBlockers', (response) => handleAcceptorStartBlockersEvents('bill', response))
}

const _resetForTest = () => {
  isMoneyModuleReady = false
  areBlockerEventActionsEnabled = false
  // @ts-expect-error - test-only
  money = undefined
}

const _resetBlockerEventActionsForTest = (): void => {
  areBlockerEventActionsEnabled = false
}

export default {
  initMoneyModule,
  getMoneyInstance,
  getAcceptorStatus,
  getBlockers,
  changeAcceptorStatus,
  changeAcceptorStatusAll,
  registerMoneyEvents,
  enableBlockerEventActions,
  _resetForTest,
  _resetBlockerEventActionsForTest
}
